import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { EdnaWrapper, getCurrentTheme } from '@providers/EdnaProvider';
import Icon from '@components/Icon';
import { TYPOGRAPHY } from '@junglescout/styles-library';
import { DropdownButtonProps } from './types';

const TRIGGER_STYLES = {
  standard: css`
    font-size: 14px;
    line-height: 24px;
    height: 36px;
    border-radius: 4px;
  `,
  small: css`
    ${TYPOGRAPHY.bodySm};
    line-height: 1;
    height: 24px;
    border-radius: 4px;
  `,
  rounded: css`
    font-size: 12px;
    line-height: 20px;
    height: 28px;
    border-radius: 14px;
  `,
};

const CaretIcon = styled(Icon)`
  min-width: 20px; // so that it doesn't get squished by its neighbouring content
  margin-left: 8px;
`;

const Trigger = styled.button<{
  disabled: boolean;
  $width?: string;
  $error: boolean;
  $triggerStyles: ReturnType<typeof css>;
  $isActive: boolean;
}>`
  all: unset;
  box-sizing: border-box;
  color: ${(props) =>
    props.disabled ? props.theme.colors.grey500 : 'inherit'};
  border: 1px solid ${(props) => props.theme.colors.grey100};
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.grey050 : props.theme.colors.white};
  width: ${(props) => props.$width};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px 0 12px;
  transition: all 0.2s ease-in-out;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  &[data-state='open'] {
    ${CaretIcon} {
      transform: rotate(180deg);
    }
  }
  &:hover:not(:disabled),
  &:active {
    outline: inherit;
    border-color: ${(props) => props.theme.colors.grey300};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.blue500};
    outline-offset: 1px;
  }
  ${(props) => props.$triggerStyles};
  ${(props) =>
    props.$isActive &&
    css`
      color: ${props.theme.colors.primary500};
      border-color: ${props.theme.colors.primary500};
      background-color: ${props.theme.colors.primary100};
      &:hover {
        border-color: ${props.theme.colors.primary500} !important;
        background-color: ${props.theme.colors.primary200};
      }
    `}
  ${(props) =>
    props.$error &&
    css`
      border-color: ${props.theme.colors.red500};
      &:hover:not(:disabled),
      &:active {
        border-color: ${(props) => props.theme.colors.red600};
      }
    `}
`;

/**
 * Use this to display a dropdown box with a caret that visibly shows open/closed state when used as a trigger in a `DropdownMenu` or `Popover`.
 *
 * This component should _only_ be used in `DropdownMenu` or `Popover`. Also used as the trigger for our `Select` component.
 *
 * Option to pass in a variant prop to change the style of the trigger. By default it is set to 'standard'.
 *
 * Pass in as children what you want to have displayed in the button.
 */

const DropdownTrigger = forwardRef<HTMLButtonElement, DropdownButtonProps>(
  (
    {
      children,
      width = '100%',
      error = false,
      disabled = false,
      variant = 'standard',
      isActive = false,
      ...props
    }: DropdownButtonProps,
    ref
  ) => {
    const { colors } = getCurrentTheme();
    const triggerStyles = TRIGGER_STYLES[variant];

    return (
      <EdnaWrapper>
        <Trigger
          {...props}
          ref={ref}
          disabled={disabled}
          $width={width}
          $error={error}
          $triggerStyles={triggerStyles}
          $isActive={isActive}
        >
          {children}
          <CaretIcon
            name="CHEVRON_DOWN"
            color={isActive ? colors.primary500 : colors.grey700}
          />
        </Trigger>
      </EdnaWrapper>
    );
  }
);

DropdownTrigger.displayName = 'DropdownTrigger';

export default DropdownTrigger;
